import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import {
  api,
  ApiError,
  clearSessionToken,
  getSessionToken,
  setSessionToken,
  type HouseReport,
  type Me,
  type NotificationPrefs,
  type Overview,
  type ShelvedCandidate,
  type ShelvedDevice,
  type TelegramLink,
  type TelegramLinkCode,
} from "./api";
import { applyTheme, getThemePref, watchSystemTheme } from "./theme";
import "./styles.css";

applyTheme(getThemePref());
watchSystemTheme();

// ---------- helpers ----------

async function copyText(text: string): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the legacy path below
    }
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    return true;
  } catch {
    return false;
  }
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

/** "august 2026" — Romanian month + year, used for each row of the history
 * list (the reports view's "year-column of the house"). */
function fmtMonthYear(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { year: "numeric", month: "long" });
  } catch {
    return iso;
  }
}

/** Bare Romanian month name ("iulie") — used in the delta chip's "față de
 * iulie" clause, where the year would be redundant. */
function fmtMonthOnly(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { month: "long" });
  } catch {
    return "";
  }
}

/** Romanian plural, the standard "1 / 2-19 / 20+" rule — mirrors the
 * backend's `romanianCount` (services/houseReport.ts). */
function roCount(n: number, one: string, many: string): string {
  if (n === 1) return `1 ${one}`;
  const mod100 = n % 100;
  if (mod100 >= 1 && mod100 < 20) return `${n} ${many}`;
  return `${n} de ${many}`;
}

/** Romanian-style number: comma decimal, never a bare "." */
function formatRoNumber(n: number, decimals: number): string {
  return n.toFixed(decimals).replace(".", ",");
}

/** Display rounding for a euro figure — mirrors the backend's
 * `formatCostEur`: whole euros at/above 10, one decimal below. */
function formatEur(n: number): string {
  if (Math.abs(n) >= 10) return Math.round(n).toString();
  return formatRoNumber(n, 1);
}

function formatKwh(n: number): string {
  return formatRoNumber(n, 1);
}

function daysLeft(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

const TELEGRAM_BOT_USERNAME = "AskMyHomeAI_bot";

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function errText(err: unknown): string {
  if (err instanceof ApiError) {
    const data = err.data as { error?: string } | null;
    if (data?.error === "invalid_credentials") return "Email sau parolă incorectă.";
    if (err.status === 429) return "Prea multe încercări. Te rugăm așteaptă un minut și încearcă din nou.";
    if (data?.error) return data.error;
  }
  return "Ceva nu a mers bine. Te rugăm încearcă din nou.";
}

// ---------- Login ----------

function LoginView({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await api.login(email.trim(), password);
      setSessionToken(token);
      onLoggedIn();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="main">
      <form class="card narrow" onSubmit={submit}>
        <h1>Conectare</h1>
        <p class="subtitle">Accesează tabloul de bord al casei tale.</p>
        {error && <div class="error-banner">{error}</div>}
        <div class="field">
          <label for="email">Email</label>
          <input
            id="email"
            type="email"
            autocomplete="email"
            required
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="password">Parolă</label>
          <input
            id="password"
            type="password"
            autocomplete="current-password"
            required
            value={password}
            onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          />
        </div>
        <button class="btn primary" type="submit" disabled={busy}>
          {busy ? "Se conectează…" : "Conectare"}
        </button>
      </form>
    </div>
  );
}

// ---------- Reports ----------

function ReportsView({ onBack }: { onBack: () => void }) {
  const [reports, setReports] = useState<HouseReport[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .reports()
      .then((r) => {
        if (!cancelled) setReports(r.reports);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errText(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div class="main top-aligned">
      <div class="card wide">
        <button class="btn subtle back-link" type="button" onClick={onBack}>
          ← Înapoi
        </button>
        <h1>Rapoarte lunare</h1>
        <p class="subtitle">Toate rapoartele pe care ți le-a trimis casa ta.</p>

        {loadError && <div class="error-banner">{loadError}</div>}
        {!reports && !loadError && <div class="spinner-wrap">Se încarcă…</div>}

        {reports && reports.length === 0 && (
          <p class="subtitle">Încă niciun raport — primul raport lunar va apărea aici.</p>
        )}

        {reports && reports.length > 0 && (
          <ul class="reports-list">
            {reports.map((r, i) => {
              const isOpen = openId === r.id;
              // Delta chip: only when THIS row and the previous (older, since
              // the list is newest-first) row both have a cost figure — a
              // client-computed comparison, no LLM involved.
              const prev = reports[i + 1];
              const delta =
                r.costEur != null && prev?.costEur != null ? Math.round(r.costEur - prev.costEur) : null;
              return (
                <li class="report-item" key={r.id}>
                  <button
                    class="report-item-toggle"
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : r.id)}
                    aria-expanded={isOpen}
                  >
                    <span class="report-item-summary">
                      <span class="report-item-month">{fmtMonthYear(r.sentAt)}</span>
                      {r.costEur != null && <span class="report-item-metric">{formatEur(r.costEur)} €</span>}
                      {r.totalKwh != null && <span class="report-item-metric">{formatKwh(r.totalKwh)} kWh</span>}
                      {delta != null && delta !== 0 && (
                        <span class={`delta-chip ${delta < 0 ? "down" : "up"}`}>
                          {delta < 0 ? "−" : "+"}
                          {Math.abs(delta)} € față de {fmtMonthOnly(prev.sentAt)}
                        </span>
                      )}
                    </span>
                    <span class="chevron">{isOpen ? "−" : "+"}</span>
                  </button>
                  {isOpen && <div class="report-item-text">{r.text}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Change password ----------

function ChangePasswordView({ onBack }: { onBack: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setSuccess(false);
    if (newPassword.length < 8) {
      setError("Parola nouă trebuie să aibă cel puțin 8 caractere.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Parola nouă și confirmarea nu se potrivesc.");
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err instanceof ApiError && (err.data as { error?: string } | null)?.error === "invalid_credentials") {
        setError("Parola actuală este incorectă.");
      } else {
        setError(errText(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="main top-aligned">
      <form class="card narrow" onSubmit={submit}>
        <button class="btn subtle back-link" type="button" onClick={onBack}>
          ← Înapoi
        </button>
        <h1>Schimbă parola</h1>
        <p class="subtitle">Schimbarea parolei deconectează celelalte dispozitive.</p>
        {error && <div class="error-banner">{error}</div>}
        {success && <div class="success-banner">Parola a fost schimbată.</div>}
        <div class="field">
          <label for="current-password">Parola actuală</label>
          <input
            id="current-password"
            type="password"
            autocomplete="current-password"
            required
            value={currentPassword}
            onInput={(e) => setCurrentPassword((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="new-password">Parolă nouă</label>
          <input
            id="new-password"
            type="password"
            autocomplete="new-password"
            required
            value={newPassword}
            onInput={(e) => setNewPassword((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label for="confirm-password">Confirmă parola nouă</label>
          <input
            id="confirm-password"
            type="password"
            autocomplete="new-password"
            required
            value={confirmPassword}
            onInput={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
          />
        </div>
        <button class="btn primary" type="submit" disabled={busy}>
          {busy ? "Se schimbă…" : "Schimbă parola"}
        </button>
      </form>
    </div>
  );
}

// ---------- Telegram ----------

function TelegramView({ onBack }: { onBack: () => void }) {
  const [links, setLinks] = useState<TelegramLink[] | null>(null);
  const [configured, setConfigured] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [linkCode, setLinkCode] = useState<TelegramLinkCode | null>(null);
  const [now, setNow] = useState(Date.now());
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  function loadLinks() {
    return api
      .telegramLinks()
      .then((r) => {
        setConfigured(r.configured);
        setLinks(r.links);
        return r.links;
      })
      .catch((err) => {
        setLoadError(errText(err));
        return null;
      });
  }

  useEffect(() => {
    loadLinks();
  }, []);

  const remainingMs = linkCode ? new Date(linkCode.expiresAt).getTime() - now : 0;
  const expired = linkCode !== null && remainingMs <= 0;

  // Countdown tick while a code is showing.
  useEffect(() => {
    if (!linkCode || expired) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [linkCode, expired]);

  // While a code is showing, poll for the chat completing "/start <code>" in
  // Telegram so the app confirms the link without the household needing to
  // come back and refresh by hand.
  useEffect(() => {
    if (!linkCode || expired) return;
    const activeBefore = (links ?? []).filter((l) => l.status === "active").length;
    const id = setInterval(() => {
      loadLinks().then((updated) => {
        if (updated && updated.filter((l) => l.status === "active").length > activeBefore) {
          setLinkCode(null);
        }
      });
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [linkCode, expired]);

  async function generateCode() {
    setGenerating(true);
    setGenError(null);
    try {
      const code = await api.telegramLinkCode();
      setLinkCode(code);
      setNow(Date.now());
    } catch (err) {
      if (err instanceof ApiError && (err.data as { error?: string } | null)?.error === "telegram_not_configured") {
        setConfigured(false);
      } else {
        setGenError(errText(err));
      }
    } finally {
      setGenerating(false);
    }
  }

  async function copyCode(code: string) {
    const ok = await copyText(code);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function revoke(id: string) {
    setRevokingId(id);
    try {
      await api.telegramRevokeLink(id);
      await loadLinks();
    } finally {
      setRevokingId(null);
    }
  }

  const activeLinks = (links ?? []).filter((l) => l.status === "active");
  const deepLink = linkCode ? `https://t.me/${TELEGRAM_BOT_USERNAME}?start=${linkCode.code}` : null;

  return (
    <div class="main top-aligned">
      <div class="card wide">
        <button class="btn subtle back-link" type="button" onClick={onBack}>
          ← Înapoi
        </button>
        <h1>Telegram</h1>
        <p class="subtitle">Primește alerte și raportul lunar pe Telegram și pune întrebări asistentului AI direct din chat.</p>

        {loadError && <div class="error-banner">{loadError}</div>}

        {links === null && !loadError && <div class="spinner-wrap">Se încarcă…</div>}

        {links !== null && !configured && (
          <div class="error-banner">Telegram nu este configurat încă pentru acest cont. Contactează instalatorul.</div>
        )}

        {links !== null && configured && (
          <>
            {!linkCode && (
              <button class="btn primary" type="button" onClick={generateCode} disabled={generating}>
                {generating ? "Se generează…" : "Conectează Telegram"}
              </button>
            )}
            {genError && (
              <div class="error-banner" style="margin-top: 1rem;">
                {genError}
              </div>
            )}

            {linkCode && !expired && deepLink && (
              <div class="telegram-code-panel">
                <ol class="telegram-steps">
                  <li>Apasă „Deschide în Telegram" — se deschide chatul cu asistentul casei.</li>
                  <li>Apasă „Start" în chat (sau trimite codul de mai jos, dacă nu apare butonul).</li>
                  <li>Așteaptă confirmarea „Legat!" — atunci ești gata.</li>
                </ol>
                <a class="btn primary" href={deepLink} target="_blank" rel="noopener noreferrer">
                  Deschide în Telegram
                </a>
                <p class="subtitle" style="margin: .9rem 0 .4rem;">
                  Sau introdu manual acest cod în chat:
                </p>
                <div class="url-row">
                  <span class="url-text telegram-code">{linkCode.code}</span>
                  <button class="btn subtle" type="button" onClick={() => copyCode(linkCode.code)}>
                    {copied ? "Copiat!" : "Copiază"}
                  </button>
                </div>
                <p class="telegram-countdown">Expiră în {formatCountdown(remainingMs)}</p>
              </div>
            )}

            {linkCode && expired && (
              <div class="telegram-code-panel">
                <p class="subtitle" style="margin-bottom: 1rem;">
                  Acest cod a expirat.
                </p>
                <button class="btn primary" type="button" onClick={generateCode} disabled={generating}>
                  {generating ? "Se generează…" : "Generează un cod nou"}
                </button>
              </div>
            )}

            <h2 class="telegram-links-heading">Chat-uri conectate</h2>
            {activeLinks.length === 0 && <p class="subtitle">Niciun chat conectat încă.</p>}
            {activeLinks.length > 0 && (
              <ul class="telegram-links-list">
                {activeLinks.map((l) => (
                  <li class="telegram-link-item" key={l.id}>
                    <span>Conectat pe {fmtDate(l.linkedAt)}</span>
                    <button class="btn subtle" type="button" onClick={() => revoke(l.id)} disabled={revokingId === l.id}>
                      {revokingId === l.id ? "Se elimină…" : "Deconectează"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Scos din priză intenționat ----------

function ShelvedView({ onBack }: { onBack: () => void }) {
  const [devices, setDevices] = useState<ShelvedDevice[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [candidates, setCandidates] = useState<ShelvedCandidate[] | null>(null);
  const [boxConnected, setBoxConnected] = useState(true);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [addingKey, setAddingKey] = useState<string | null>(null);

  function loadDevices() {
    return api
      .shelvedDevices()
      .then((r) => setDevices(r.devices))
      .catch((err) => setLoadError(errText(err)));
  }

  useEffect(() => {
    loadDevices();
  }, []);

  async function startAdding() {
    setAdding(true);
    setCandidatesError(null);
    try {
      const r = await api.shelvedCandidates();
      setCandidates(r.candidates);
      setBoxConnected(r.boxConnected);
    } catch (err) {
      setCandidatesError(errText(err));
    }
  }

  async function addDevice(candidate: ShelvedCandidate) {
    setAddingKey(candidate.key);
    try {
      const r = await api.shelveDevice(candidate.key, candidate.label);
      setDevices(r.devices);
      setCandidates((prev) => (prev ? prev.filter((c) => c.key !== candidate.key) : prev));
      setAdding(false);
    } catch (err) {
      setCandidatesError(errText(err));
    } finally {
      setAddingKey(null);
    }
  }

  async function removeDevice(key: string) {
    setRemovingKey(key);
    try {
      const r = await api.unshelveDevice(key);
      setDevices(r.devices);
    } catch (err) {
      setLoadError(errText(err));
    } finally {
      setRemovingKey(null);
    }
  }

  const shelvedKeys = new Set((devices ?? []).map((d) => d.key));
  const addableCandidates = (candidates ?? []).filter((c) => !shelvedKeys.has(c.key));

  return (
    <div class="main top-aligned">
      <div class="card wide">
        <button class="btn subtle back-link" type="button" onClick={onBack}>
          ← Înapoi
        </button>
        <h1>Aparate scoase intenționat</h1>
        <p class="subtitle">
          Aspiratorul e strâns pe vară? Marchează-l și nu-l mai numărăm printre probleme până revine — de îndată ce raportează din nou o
          stare reală, marcajul se șterge singur și te anunțăm.
        </p>

        {loadError && <div class="error-banner">{loadError}</div>}

        {devices === null && !loadError && <div class="spinner-wrap">Se încarcă…</div>}

        {devices !== null && (
          <>
            {devices.length === 0 && !adding && <p class="subtitle">Niciun aparat marcat momentan.</p>}

            {devices.length > 0 && (
              <ul class="telegram-links-list">
                {devices.map((d) => (
                  <li class="telegram-link-item" key={d.key}>
                    <span>
                      {d.label} <span class="subtitle">— marcat pe {fmtDate(d.shelvedAt)}</span>
                    </span>
                    <button class="btn subtle" type="button" onClick={() => removeDevice(d.key)} disabled={removingKey === d.key}>
                      {removingKey === d.key ? "Se elimină…" : "Scoate marcajul"}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!adding && (
              <button class="btn primary" type="button" style="margin-top: 1rem;" onClick={startAdding}>
                Marchează un aparat
              </button>
            )}

            {adding && (
              <div class="telegram-code-panel" style="margin-top: 1rem;">
                {candidatesError && <div class="error-banner">{candidatesError}</div>}
                {candidates === null && !candidatesError && <div class="spinner-wrap">Se caută aparate…</div>}

                {candidates !== null && !boxConnected && (
                  <p class="subtitle">Casa nu e conectată chiar acum, așa că nu putem căuta aparate live. Încearcă din nou mai târziu.</p>
                )}

                {candidates !== null && boxConnected && addableCandidates.length === 0 && (
                  <p class="subtitle">Niciun aparat nu răspunde chiar acum — nimic de marcat.</p>
                )}

                {addableCandidates.length > 0 && (
                  <ul class="telegram-links-list">
                    {addableCandidates.map((c) => (
                      <li class="telegram-link-item" key={c.key}>
                        <span>{c.label}</span>
                        <button class="btn subtle" type="button" onClick={() => addDevice(c)} disabled={addingKey === c.key}>
                          {addingKey === c.key ? "Se marchează…" : "Marchează"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <button class="btn subtle" type="button" style="margin-top: .8rem;" onClick={() => setAdding(false)}>
                  Renunță
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Notificări (Vesta cycle-2 proposal 4) ----------

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);

function Toggle({ checked, onChange, disabled, label }: { checked: boolean; onChange?: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <label class={`toggle-switch${disabled ? " disabled" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange?.((e.target as HTMLInputElement).checked)}
      />
      <span class="toggle-track">
        <span class="toggle-thumb" />
      </span>
    </label>
  );
}

function NotificationsView({ onBack }: { onBack: () => void }) {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .notificationPrefs()
      .then((r) => {
        if (!cancelled) setPrefs(r.prefs);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errText(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(next: NotificationPrefs) {
    const previous = prefs;
    setPrefs(next); // optimistic
    setSaveError(null);
    try {
      const { prefs: stored } = await api.setNotificationPrefs(next);
      setPrefs(stored);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setPrefs(previous); // revert
      setSaveError(errText(err));
    }
  }

  return (
    <div class="main top-aligned">
      <div class="card wide">
        <button class="btn subtle back-link" type="button" onClick={onBack}>
          ← Înapoi
        </button>
        <h1>Notificări</h1>
        <p class="subtitle">Ce primești pe Telegram — casa continuă să te anunțe la fel de bine în aplicație și în vocea din casă.</p>

        {loadError && <div class="error-banner">{loadError}</div>}
        {saveError && <div class="error-banner">{saveError}</div>}
        {saved && <div class="success-banner">Salvat</div>}

        {prefs === null && !loadError && <div class="spinner-wrap">Se încarcă…</div>}

        {prefs && (
          <ul class="notif-rows">
            <li class="notif-row">
              <div class="notif-row-text">
                <span class="notif-row-title">Raportul lunar</span>
                <span class="notif-row-desc">Raportul AI despre casă, o dată pe lună.</span>
              </div>
              <Toggle checked={prefs.monthlyReport} label="Raportul lunar" onChange={(v) => save({ ...prefs, monthlyReport: v })} />
            </li>

            <li class="notif-row">
              <div class="notif-row-text">
                <span class="notif-row-title">Observații despre casă (inițiative)</span>
                <span class="notif-row-desc">Ce observă și îți semnalează asistentul, pe măsură ce apare.</span>
              </div>
              <Toggle checked={prefs.initiative} label="Observații despre casă" onChange={(v) => save({ ...prefs, initiative: v })} />
            </li>

            <li class="notif-row">
              <div class="notif-row-text">
                <span class="notif-row-title">Aparate revenite la viață</span>
                <span class="notif-row-desc">Când un aparat marcat „scos intenționat" raportează din nou o stare reală.</span>
              </div>
              <Toggle checked={prefs.revival} label="Aparate revenite la viață" onChange={(v) => save({ ...prefs, revival: v })} />
            </li>

            <li class="notif-row">
              <div class="notif-row-text">
                <span class="notif-row-title">Alerta «casa nu mai răspunde»</span>
                <span class="notif-row-desc">Mereu activă — e alerta care contează exact când nu ești atent.</span>
              </div>
              <Toggle checked={true} disabled label="Alerta casa nu mai răspunde (mereu activă)" />
            </li>
          </ul>
        )}

        {prefs && (
          <>
            <h2 class="telegram-links-heading">Oră de liniște</h2>
            <ul class="notif-rows">
              <li class="notif-row">
                <div class="notif-row-text">
                  <span class="notif-row-title">Nu mă anunța într-un anumit interval</span>
                  <span class="notif-row-desc">
                    Nu se aplică raportului lunar și alertei „casa nu mai răspunde" — acelea ajung oricând.
                  </span>
                </div>
                <Toggle
                  checked={prefs.quietHours.enabled}
                  label="Oră de liniște"
                  onChange={(v) => save({ ...prefs, quietHours: { ...prefs.quietHours, enabled: v } })}
                />
              </li>
            </ul>

            {prefs.quietHours.enabled && (
              <div class="quiet-hours-range">
                <div class="field quiet-hours-field">
                  <label for="quiet-start">De la ora</label>
                  <select
                    id="quiet-start"
                    value={prefs.quietHours.startHour}
                    onChange={(e) => save({ ...prefs, quietHours: { ...prefs.quietHours, startHour: Number((e.target as HTMLSelectElement).value) } })}
                  >
                    {HOUR_OPTIONS.map((h) => (
                      <option value={h} key={h}>
                        {h.toString().padStart(2, "0")}:00
                      </option>
                    ))}
                  </select>
                </div>
                <div class="field quiet-hours-field">
                  <label for="quiet-end">Până la ora</label>
                  <select
                    id="quiet-end"
                    value={prefs.quietHours.endHour}
                    onChange={(e) => save({ ...prefs, quietHours: { ...prefs.quietHours, endHour: Number((e.target as HTMLSelectElement).value) } })}
                  >
                    {HOUR_OPTIONS.map((h) => (
                      <option value={h} key={h}>
                        {h.toString().padStart(2, "0")}:00
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Overview ----------

function serviceBadge(service: Overview["service"]) {
  if (!service.allowed) {
    if (service.reason === "trial_expired") return <span class="badge danger">Perioadă de probă expirată</span>;
    if (service.reason === "suspended") return <span class="badge danger">Suspendat</span>;
    if (service.reason === "cancelled") return <span class="badge danger">Anulat</span>;
    return <span class="badge warn">Inactiv</span>;
  }
  if (service.reason === "trial") {
    const left = daysLeft(service.trialEndsAt);
    return <span class="badge info">Perioadă de probă{left !== null ? ` — mai sunt ${roCount(left, "zi", "zile")}` : ""}</span>;
  }
  return <span class="badge ok">Activ</span>;
}

function OverviewView({
  me,
  onLoggedOut,
  onOpenReports,
  onOpenChangePassword,
  onOpenTelegram,
  onOpenShelved,
  onOpenNotifications,
}: {
  me: Me;
  onLoggedOut: () => void;
  onOpenReports: () => void;
  onOpenChangePassword: () => void;
  onOpenTelegram: () => void;
  onOpenShelved: () => void;
  onOpenNotifications: () => void;
}) {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .overview()
      .then((o) => {
        if (!cancelled) setOverview(o);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errText(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function logout() {
    setLoggingOut(true);
    try {
      await api.logout();
    } catch {
      // best-effort — the local token is cleared regardless (mirrors
      // ai-guidance's reference dashboard's manualLogout)
    }
    clearSessionToken();
    onLoggedOut();
  }

  async function copyUrl(url: string) {
    const ok = await copyText(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div class="main top-aligned">
      <div class="overview-grid">
        <div class="card wide">
          <h1>Bine ai venit{me.installation.name ? `, ${me.installation.name}` : ""}</h1>
          <p class="subtitle">{me.email}</p>

          {loadError && <div class="error-banner">{loadError}</div>}

          {!overview && !loadError && <div class="spinner-wrap">Se încarcă…</div>}

          {overview && (
            <>
              <div class="status-row">
                {serviceBadge(overview.service)}
                <span class="conn-row">
                  <span class={`dot ${overview.tunnelLive ? "live" : "dead"}`} />
                  {overview.tunnelLive ? "Conectat" : "Neconectat"}
                </span>
              </div>

              {overview.url ? (
                <div class="url-row">
                  <span class="url-text">{overview.url}</span>
                  <button class="btn subtle" type="button" onClick={() => copyUrl(overview.url!)}>
                    {copied ? "Copiat!" : "Copiază"}
                  </button>
                  <a class="btn ghost" href={overview.url} target="_blank" rel="noopener noreferrer">
                    Deschide
                  </a>
                </div>
              ) : (
                <p class="subtitle" style="margin-top: 1rem;">
                  Accesul de la distanță nu a fost configurat încă pentru casa ta. Contactează instalatorul.
                </p>
              )}
            </>
          )}
        </div>

        <div class={`report-card${overview?.latestReport ? "" : " empty"}`}>
          {overview?.latestReport ? (
            <>
              <h2>Ultimul raport lunar</h2>
              <div class="report-date">
                {fmtDate(overview.latestReport.sentAt)}
                {overview.latestReport.costEur != null && (
                  <span class="report-date-metric"> · {formatEur(overview.latestReport.costEur)} €</span>
                )}
              </div>
              <div class="report-text">{overview.latestReport.text}</div>
            </>
          ) : (
            overview && <span>Încă niciun raport — revino după prima lună.</span>
          )}
        </div>

        <div class="footer-row">
          <button class="btn subtle" type="button" onClick={onOpenTelegram}>
            Telegram
          </button>
          <button class="btn subtle" type="button" onClick={onOpenReports}>
            Rapoarte
          </button>
          <button class="btn subtle" type="button" onClick={onOpenShelved}>
            Aparate scoase intenționat
          </button>
          <button class="btn subtle" type="button" onClick={onOpenNotifications}>
            Notificări
          </button>
          <button class="btn subtle" type="button" onClick={onOpenChangePassword}>
            Schimbă parola
          </button>
          <button class="btn subtle" type="button" onClick={logout} disabled={loggingOut}>
            {loggingOut ? "Se deconectează…" : "Deconectare"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- App shell ----------

type Screen = "overview" | "reports" | "changePassword" | "telegram" | "shelved" | "notifications";

function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined); // undefined = loading, null = logged out
  const [screen, setScreen] = useState<Screen>("overview");

  function refreshMe() {
    if (!getSessionToken()) {
      setMe(null);
      return;
    }
    api
      .me()
      .then((m) => setMe(m))
      .catch(() => {
        clearSessionToken();
        setMe(null);
      });
  }

  useEffect(refreshMe, []);

  function loggedOut() {
    setScreen("overview");
    setMe(null);
  }

  return (
    <div class="shell">
      <div class="topbar">
        <span class="wordmark">AskMyHomeAI</span>
      </div>
      {me === undefined && <div class="spinner-wrap">Se încarcă…</div>}
      {me === null && <LoginView onLoggedIn={refreshMe} />}
      {me && screen === "overview" && (
        <OverviewView
          me={me}
          onLoggedOut={loggedOut}
          onOpenReports={() => setScreen("reports")}
          onOpenChangePassword={() => setScreen("changePassword")}
          onOpenTelegram={() => setScreen("telegram")}
          onOpenShelved={() => setScreen("shelved")}
          onOpenNotifications={() => setScreen("notifications")}
        />
      )}
      {me && screen === "reports" && <ReportsView onBack={() => setScreen("overview")} />}
      {me && screen === "changePassword" && <ChangePasswordView onBack={() => setScreen("overview")} />}
      {me && screen === "telegram" && <TelegramView onBack={() => setScreen("overview")} />}
      {me && screen === "shelved" && <ShelvedView onBack={() => setScreen("overview")} />}
      {me && screen === "notifications" && <NotificationsView onBack={() => setScreen("overview")} />}
    </div>
  );
}

render(<App />, document.getElementById("root")!);
